import React, { useMemo } from 'react';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { CloseOutline } from '@lyearn/icons';
import { Box, Input, LinkButton, Select } from '@lyearn/ui';
import { getSelectOptionLabel } from '@/components/Form/components/Select/getOptionLabel';
import { ReminderConfigurationType, ReminderConfigurationUnitTypeEnum } from '@/types/schema';

import { ReminderUnits, ReminderUnitsConfig } from '../conts';

const getValue = (item: ReminderConfigurationUnitTypeEnum, t: TFunction) => ({
  value: item,
  label: t(ReminderUnitsConfig[item]),
});

const getOptions = (items: ReminderConfigurationUnitTypeEnum[], t: TFunction) =>
  items.map((item) => getValue(item, t));

type ReminderDialogRow = {
  index: number;
  updateReminder: (index: number, reminderConfig: Partial<ReminderConfigurationType>) => void;
  deleteReminder: (index: number) => void;
  reminderConfig: Partial<ReminderConfigurationType>;
  showError: boolean;
};

const ReminderDialogRow: React.FC<ReminderDialogRow> = React.memo((props) => {
  const { index, reminderConfig, updateReminder, deleteReminder, showError } = props;
  const { t } = useTranslation();

  const options = useMemo(() => getOptions(ReminderUnits, t), [t]);

  return (
    <Box className="mb-8 flex h-40 w-full flex-row items-center gap-12">
      <Input
        className="flex-1"
        error={showError && !reminderConfig.value}
        multiline={false}
        placeholder="0"
        size="medium"
        type="number"
        value={reminderConfig.value}
        onChange={(event) => {
          updateReminder(index, {
            ...reminderConfig,
            value: event.target.value ? Number(event.target.value) : undefined,
          });
        }}
      />
      <Select
        disableClearable
        className="flex-1"
        getOptionLabel={getSelectOptionLabel}
        options={options}
        size="medium"
        value={reminderConfig.unit!}
        onChange={(value) => {
          updateReminder(index, {
            ...reminderConfig,
            unit: value as ReminderConfigurationUnitTypeEnum,
          });
        }}
      />
      <LinkButton className="ms-12" color={'internal'} onClick={() => deleteReminder(index)}>
        <CloseOutline className="h-24 w-24" />
      </LinkButton>
    </Box>
  );
});

export default ReminderDialogRow;
